import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Calendar, Tag, ArrowRight, Clock } from "lucide-react";
import DOMPurify from "dompurify";
import { fetchPost } from "@/lib/api";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import NewsletterForm from "@/components/NewsletterForm";
import useSEO from "@/lib/useSEO";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; }
};

// Lightweight markdown rendering for our limited blog body format.
// HTML fragments produced by inline() are sanitized with DOMPurify before injection.
const SAN_CONFIG = { ALLOWED_TAGS: ["strong", "a", "em"], ALLOWED_ATTR: ["href"] };
const sanitize = (s) => DOMPurify.sanitize(s, SAN_CONFIG);

function renderBody(body) {
  const lines = body.split("\n");
  const out = [];
  let listBuf = [];
  let listType = null;
  const flushList = () => {
    if (!listBuf.length) return;
    out.push(
      listType === "ol" ? (
        <ol key={`l-${out.length}`}>{listBuf.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: sanitize(li) }} />)}</ol>
      ) : (
        <ul key={`l-${out.length}`}>{listBuf.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: sanitize(li) }} />)}</ul>
      )
    );
    listBuf = [];
    listType = null;
  };
  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>');

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flushList(); return; }
    if (line.startsWith("## ")) {
      flushList();
      out.push(<h2 key={idx}>{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      flushList();
      out.push(<h3 key={idx}>{line.slice(4)}</h3>);
    } else if (/^\d+\.\s/.test(line)) {
      if (listType && listType !== "ol") flushList();
      listType = "ol";
      listBuf.push(inline(line.replace(/^\d+\.\s/, "")));
    } else if (line.startsWith("- ")) {
      if (listType && listType !== "ul") flushList();
      listType = "ul";
      listBuf.push(inline(line.slice(2)));
    } else {
      flushList();
      out.push(<p key={idx} dangerouslySetInnerHTML={{ __html: sanitize(inline(line)) }} />);
    }
  });
  flushList();
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: post?.seo_title || (post ? `${post.title} | Upload Counsel` : "Article | Upload Counsel"),
    description: post?.seo_description || post?.excerpt,
    canonical: post ? `https://uploadlawyer.com/blog/${post.slug}` : undefined,
    image: post?.cover_image,
  });

  useEffect(() => {
    (async () => {
      setPost(null);
      setNotFound(false);
      try {
        const data = await fetchPost(slug);
        setPost(data.post);
        setRelated(data.related || []);
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-post-notfound">
        <h1 className="font-display text-4xl font-bold text-white">Post not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-1 text-[#D9B36C]">
          <ChevronLeft size={14} /> Back to blog
        </Link>
      </div>
    );
  }
  if (!post) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-slate-500" data-testid="blog-post-loading">Loading…</div>;
  }

  // Reading time (~230 words/min)
  const wordCount = (post.body || "").split(/\s+/).length;
  const readingMin = Math.max(1, Math.round(wordCount / 230));

  // BlogPosting JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.seo_description || post.excerpt,
    "image": post.cover_image,
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "author": { "@type": "Organization", "name": post.author || "Upload Counsel" },
    "publisher": {
      "@type": "Organization",
      "name": "Upload Counsel",
      "logo": { "@type": "ImageObject", "url": "https://uploadlawyer.com/og-image.png" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://uploadlawyer.com/blog/${post.slug}` },
    "keywords": (post.tags || []).join(", "),
    "articleSection": post.category,
  };

  // Split body roughly in half for mid-post CTA
  const bodyChildren = renderBody(post.body || "");
  const midpoint = Math.floor(bodyChildren.length / 2);
  const firstHalf = bodyChildren.slice(0, midpoint);
  const secondHalf = bodyChildren.slice(midpoint);

  return (
    <div data-testid={`page-blog-post-${slug}`}>
      <article className="relative">
        {/* Cover */}
        {post.cover_image && (
          <div className="relative aspect-[21/9] md:aspect-[21/7] overflow-hidden">
            <img src={post.cover_image} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/40 to-[#0B0C10]/40" />
          </div>
        )}

        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 -mt-20 md:-mt-28">
          <div className="rounded-3xl border border-[#272B36] bg-[#0B0C10]/90 backdrop-blur p-8 md:p-12 shadow-2xl">
            <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-[#D9B36C]">
              <ChevronLeft size={14} /> Back to blog
            </Link>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D9B36C]/15 border border-[#D9B36C]/30 text-[#D9B36C] text-[10px] font-semibold uppercase tracking-widest">
                <Tag size={10} /> {post.category}
              </span>
              {post.tags?.slice(0, 3).map((t) => (
                <span key={t} className="text-xs text-slate-500">#{t}</span>
              ))}
            </div>

            <h1 className="mt-4 font-display text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
              {post.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-400">
              <span className="inline-flex items-center gap-2">
                <span className="inline-flex h-8 w-8 rounded-full bg-gradient-to-br from-[#D9B36C] to-[#7C8DB0] items-center justify-center text-black font-bold text-xs">U</span>
                {post.author}
              </span>
              <span className="inline-flex items-center gap-1"><Calendar size={14} /> {formatDate(post.published_at)}</span>
              <span className="inline-flex items-center gap-1"><Clock size={14} /> {readingMin} min read</span>
            </div>

            <div className="mt-10 blog-prose">
              {firstHalf}

              {/* Mid-post consultation CTA */}
              <div className="my-10 not-prose rounded-2xl border border-[#D9B36C]/30 bg-gradient-to-br from-[#14161D] to-[#121419] p-6" data-testid="mid-post-cta">
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Facing this yourself?</p>
                <p className="mt-2 font-display font-semibold text-white text-lg leading-tight">Send a request. A licensed attorney reads it, free, and we aim to reply within one business day.</p>
                <Link to="/start" data-testid="mid-post-cta-link" className="mt-4 inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-5 py-2.5 rounded-full text-sm">
                  Start a request <ArrowRight size={14} />
                </Link>
              </div>

              {secondHalf}
            </div>

            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

            <LegalDisclaimer />

            {/* Inline newsletter */}
            <div className="mt-10 rounded-2xl border border-[#272B36] bg-gradient-to-br from-[#14161D] to-[#121419] p-6">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">The Brief</p>
              <p className="mt-2 font-display font-semibold text-white text-lg">More playbooks like this — in your inbox.</p>
              <div className="mt-4">
                <NewsletterForm source={`post-${post.slug}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Related (internal linking) */}
        {related.length > 0 && (
          <section className="relative py-20 mt-12 border-t border-[#272B36]">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Related reading</p>
              <h2 className="mt-3 font-display text-2xl font-bold text-white tracking-tight">Continue the playbook</h2>
              <div className="mt-8 space-y-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    data-testid={`related-post-${r.slug}`}
                    className="group block rounded-2xl border border-[#272B36] bg-[#121419] p-5 hover:border-[#D9B36C]/50 transition-colors"
                  >
                    <p className="text-xs uppercase tracking-widest text-[#D9B36C] font-semibold">{r.category}</p>
                    <p className="mt-2 font-display font-semibold text-white">{r.title}</p>
                    <p className="mt-2 text-sm text-slate-400">{r.excerpt}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm text-[#D9B36C] group-hover:gap-2 transition-all">
                      Read <ArrowRight size={14} />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </div>
  );
}
